import { IParamsCreateComponent } from '@/courses/interfaces/params-create-component.interface';
import Vue from 'vue';

export default Vue.component('ed-select-custom-multiple', { 
  name: 'ed-select-custom-multiple',
  props: {
    options: {
      type: Array as () => Array<string | number | { text: string; value: any }>,
      required: true,
    },
    value: {
      type: [String, Number, Array, Object],
      default: () => [],
    },
    placeholder: {
      type: String,
      default: '',
    },
    rules: {
      type: Array as () => Array<(value: any) => boolean | string>,
      default: () => [],
    },
    hint: {
      type: String,
      default: '',
    },
    persistentHint: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    itemText: {
      type: String,
      default: 'text',
    },
    itemValue: {
      type: String,
      default: 'value',
    },
    itemDisabled: {
      type: String,
      default: 'disabled',
    },
    returnObject: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: true,
    },
    chips: {
      type: Boolean,
      default: false,
    },
    showOnlyTheNumberChips: {
      type: Boolean,
      default: false,
    },
    showAsteriskRequired: {
      type: Boolean,
      default: false,
    },
    hintWarning: {
      type: Boolean,
      default: false,
    },
    chipActive: {
      type: Boolean,
      default: false,
    },
    backgroundChip: {
      type: String,
      default: '#e0e0e0',
    },
    textColorChip: {
      type: String,
      default: '#111827',
    },
    backgroundChipActive: {
      type: String,
      default: '#e0e0e0',
    },
    textColorChipActive: {
      type: String,
      default: '#111827',
    },
  },
  data() {
    return {
      isOpen: false,
      isFocused: false,
      selectedValue: [],
      highlightedIndex: -1,
      errorMessage: '',
      dropUp: false,
      windowIsFocused: true,
    };
  },
  computed: {
    hasError(): boolean {
      return this.errorMessage !== '';
    },
    shouldShowHint(): boolean {
      return this.persistentHint || this.isFocused;
    },
    scrollHandlers(): {type: string, listener: any}[] {
      return [
        { type: 'wheel', listener: this.handleWheelEvent },
      ];
    },
  },
  watch: {
    value() {
      this.syncSelectedValue();
      this.validate();
    },
    isOpen() {
      this.setScrollHandlers(this.isOpen);
      if (this.rules?.length > 0 && !this.isOpen) {
        this.validate();
      }
    },
    options() {
      this.syncSelectedValue();
    }
  },
  created() {
    this.syncSelectedValue();
  },
  methods: {
    resolveCssValue(value: string) {
      return value.startsWith('--') ? `var(${value})` : value;
    },
    getChipStyle(option: any) {
      const fullOption = typeof option === 'object' ? option : this.options.find(opt => this.getValue(opt) === option);
      const isMandatory = fullOption && fullOption[this.itemDisabled];

      return {
        backgroundColor: this.resolveCssValue(isMandatory ? this.backgroundChip : this.backgroundChipActive),
        color: this.resolveCssValue(isMandatory ? this.textColorChip : this.textColorChipActive),
      };
    },
    toggleDropdown() {
      if (this.disabled) return;
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.highlightSelected();
        this.$nextTick(() => {
          this.scrollToHighlighted();
        });

        this.handleDropUp();
      }
    },
    handleDropUp() {
      this.$nextTick(() => {
        const selectEl = this.$refs.select as HTMLElement;
        const dropdownEl = selectEl.querySelector('.custom-select-underlined__dropdown') as HTMLElement;

        if (!selectEl || !dropdownEl) return;

        const rect = selectEl.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const dropdownHeight = dropdownEl.offsetHeight;
        this.dropUp = spaceBelow < dropdownHeight;
      });
    },
    selectOption(option: string | number | { text: string; value: any }) {
      if (this.disabled) return;
      const value = this.getValue(option);
      const isSelected = this.selectedValue.includes(value);

      const isOptionDisabled = typeof option === 'object' && option[this.itemDisabled];

      if (isSelected && isOptionDisabled) {
        return;
      }

      if (this.multiple) {
        const index = this.selectedValue.indexOf(value);
        if (index === -1) {
          this.selectedValue.push(value);
        } else {
          this.selectedValue.splice(index, 1);
        }
      } else {
        this.selectedValue = [value];
        this.isOpen = false;
      }
      const selected = this.returnObject ? this.options.find(opt => this.getValue(opt) === value) : (this.multiple ? this.selectedValue : this.selectedValue[0]);
      this.$emit('input', selected);
      this.$emit('change', selected);
      this.validate();
    },
    highlightIndex(index: number) {
      this.highlightedIndex = index;
    },
    highlightSelected() {
      const selectedValue = this.selectedValue[0];
      const index = this.options.findIndex(
        option => this.getValue(option) === selectedValue
      );
      this.highlightedIndex = index >= 0 ? index : -1;
    },
    handleKeydown(event: KeyboardEvent) {
      if (this.disabled) return;
      if (!this.isOpen) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          this.toggleDropdown();
        }
        return;
      }

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.highlightedIndex =
            (this.highlightedIndex + 1) % this.options.length;
          this.scrollToHighlighted();
          break;

        case 'ArrowUp':
          event.preventDefault();
          this.highlightedIndex =
            (this.highlightedIndex - 1 + this.options.length) %
            this.options.length;
          this.scrollToHighlighted();
          break;

        case 'Enter':
          event.preventDefault();
          if (this.highlightedIndex >= 0) {
            this.selectOption(this.options[this.highlightedIndex]);
          }
          break;

        case 'Escape':
          event.preventDefault();
          this.isOpen = false;
          break;
      }
    },
    handleFocus() {
      if (this.disabled) return;
      this.isFocused = true;
    },
    handleBlur() {
      if (this.disabled) return;
      setTimeout(() => {
        if (!this.windowIsFocused) {
          return;
        }
        this.isFocused = false;
        this.isOpen = false;
      }, 100);
    },
    scrollToHighlighted() {
      const dropdown = this.$refs.select as HTMLElement;
      const highlighted = dropdown.querySelector(
        '.custom-select-underlined__option--highlighted'
      ) as HTMLElement;

      if (highlighted) {
        highlighted.scrollIntoView({
          block: 'nearest',
        });
      }
    },
    validate() {
      for (const rule of this.rules) {
        const result = rule(this.selectedValue);
        if (result === false || typeof result === 'string') {
          this.errorMessage = typeof result === 'string' ? result : 'Invalid value.';
          return;
        }
      }
      this.errorMessage = '';
    },
    getDisplayText(option: any): string {
      return typeof option === 'object' ? option[this.itemText] : String(option);
    },
    getValue(option: any): any {
      return typeof option === 'object' ? option[this.itemValue] : option;
    },
    getDisplayTextFromValue(value: any): string {
      if (this.returnObject) {
        const match = this.options.find(opt => this.getValue(opt) === this.getValue(value));
        return match ? this.getDisplayText(match) : '';
      } else {
        const option = this.options.find(opt => this.getValue(opt) === value);
        return option ? this.getDisplayText(option) : null;
      }
    },
    isOptionSelected(option: any): boolean {
      return this.selectedValue.includes(this.getValue(option));
    },
    syncSelectedValue() {
      if (this.returnObject) {
        this.selectedValue = Array.isArray(this.value)
          ? this.value.map(v => this.options.find(opt => this.getValue(opt) === this.getValue(v))).filter(Boolean)
          : [this.options.find(opt => this.getValue(opt) === this.getValue(this.value))].filter(Boolean);
      } else {
        this.selectedValue = Array.isArray(this.value) ? this.value : [this.value];
      }
    },
    getDropDownList(): HTMLElement | null {
      return this.$el.querySelector('.custom-select-underlined__dropdown');
    },
    handleWheelEvent(event: WheelEvent): void {
      const dropdownList = this.getDropDownList();
      if (!this.isOpen || !dropdownList) {
        return;
      }

      if (!this.$el.contains(event.target)) {
        event.preventDefault();
        return;
      }

      const scroll = {
        isScrollingUp: event.deltaY < 0,
        isScrollingDown: event.deltaY > 0,
        isAtTop: dropdownList.scrollTop <= 0,
        isAtBottom: Math.abs(dropdownList.scrollHeight - dropdownList.clientHeight - dropdownList.scrollTop) < 1,
      };

      // Prevent scroll only at boundaries
      if ((scroll.isScrollingUp && scroll.isAtTop) || (scroll.isScrollingDown && scroll.isAtBottom)) {
        event.preventDefault();
      }
    },
    setScrollHandlers(turnOn: boolean): void {
      this.scrollHandlers?.forEach((handler: {type: string, listener: any}) => {
        if (turnOn) {
          window.addEventListener(handler.type, handler.listener, { passive: false });
        } else {
          window.removeEventListener(handler.type, handler.listener);
        }
      });
    },
    focusWindow() {
      this.windowIsFocused = true;
    },
    unfocusWindow() {
      this.windowIsFocused = false;
    },
  },
  /* Hooks */
  mounted() {
    window.addEventListener('focus', this.focusWindow);
    window.addEventListener('blur', this.unfocusWindow);
  },
  beforeDestroy() {
    this.setScrollHandlers(false);
    window.removeEventListener('focus', this.focusWindow);
    window.removeEventListener('blur', this.unfocusWindow);
  },
});